class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

// Lets desktop nav items open their dropdown/mega-menu on hover instead of only on click, while the
// top-level label itself is now a real link (see header-dropdown-menu.liquid / header-mega-menu.liquid)
// that navigates on click. Hover-intent is guarded to mouse/trackpad input via a (hover: hover) and
// (pointer: fine) media check, so touch devices are untouched and keep their tap-to-open behaviour.
// mouseleave uses a short grace-period timeout (cleared on re-entry) rather than closing immediately,
// so moving the cursor from the top-level label down into the submenu panel below it doesn't
// prematurely close the menu. Uses the details element's real `open` attribute (not a class or
// inline style) so all of the existing open/close CSS animation and JS (onToggle, focusout-to-close,
// Escape-to-close) keeps working unmodified, whether the toggle was triggered by hover, click, or
// keyboard.
//
// onMouseEnter also immediately closes every OTHER header-menu instance (not just this one) rather
// than leaving them to their own mouseleave timers. Without that, moving the cursor directly from one
// open top-level item to the next left the previous dropdown visibly open for the same ~150ms grace
// period meant for "moving down into this item's own panel" — the two panels would briefly overlap,
// reading as sluggish/stuck-open. Calling close() on a sibling here is safe even if its own delayed
// close() from its mouseleave timer fires moments later — close() is idempotent.
class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');

    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
  }

  onMouseEnter() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    clearTimeout(this.mouseLeaveTimeout);

    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu !== this) menu.close();
    });

    if (!this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.setAttribute('open', '');
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
    }
  }

  onMouseLeave() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    clearTimeout(this.mouseLeaveTimeout);
    this.mouseLeaveTimeout = setTimeout(() => {
      this.close();
    }, 150);
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
